// Learnosity configuration templates

export const standardLearnosityConfig = {
  questions_api_init_options: {
    show_distractor_rationale: {
      per_question: "always",
      per_response: "always",
    },
  },
  configuration: {
    onsubmit_redirect_url: false,
    idle_timeout: false,
    submit_criteria: {
      type: "attempted",
      threshold: 100,
    },
  },
  navigation: {
    auto_save: {
      save_interval_duration: 30,
      ui: true,
    },
    show_intro: false,
    show_outro: false,
    scrolling_indicator: true,
    warning_on_change: false,
    skip_submit_confirmation: true,
  },
  regions: {
    "top-left": [],
    "top-right": [],
    right: [],
    "bottom-right": [
      { type: "next_button" },
      { type: "horizontaltoc_element" },
      { type: "previous_button" },
    ],
    items: [{ type: "progress_element" }, { type: "slider_element", scrollable_option: false }],
    "bottom-left": [{ type: "accessibility_button" }],
    bottom: [],
  },
  title: "All About Interviews",
  labelBundle: {
    confirm: "Okay",
    continue: "Okay",
    accessibility: "",
    warningOnChangeHeadingMsg: "Hang on!",
    warningOnChangeBodyMsg: "Complete all questions before you continue.",
    warningOnChangeCancelButton: "Go Back",
    warningOnChangeContinueButton: "Continue Anyway",
    submitButtonLabel: "Done",
    submitCriteriaNotMet: "You skipped at least one question",
    submitTest: "You missed something...",
    submitCriteriaLessThanRequiredAttemptedQuestionsWarning:
      "Go back and answer {{numberOfMoreQuestionsNeeded}} question(s) to complete the Ladder.",
  },
};

export const gameLearnosityConfig = {
  ...standardLearnosityConfig,
  regions: {
    "top-left": [],
    "top-right": [],
    right: [],
    "bottom-right": [{ type: "next_button" }],
    items: [{ type: "slider_element", scrollable_option: false }],
    "bottom-left": [],
    bottom: [],
  },
  labelBundle: {
    confirm: "Okay",
    continue: "Okay",
    accessibility: " ",
    warningOnChangeHeadingMsg: "Hang on!",
    warningOnChangeBodyMsg: "Complete all questions before you continue.",
    warningOnChangeCancelButton: "Go Back",
    warningOnChangeContinueButton: "Continue Anyway",
    nextButtonLabel: " ",
    submitButtonLabel: "Done",
    submitCriteriaNotMet: "You must complete the game to complete the Ladder.",
    submitTest: "You missed something...",
    submitCriteriaLessThanRequiredAttemptedQuestionsWarning: "",
  },
};

export const profilerLearnosityConfig = {
  ...standardLearnosityConfig,
  regions: {
    "top-left": [],
    "top-right": [],
    right: [],
    "bottom-right": [{ type: "next_button" }, { type: "previous_button" }],
    items: [{ type: "progress_element" }, { type: "slider_element", scrollable_option: false }],
    "bottom-left": [{ type: "accessibility_button" }],
    bottom: [],
  },
  labelBundle: {
    confirm: "Okay",
    continue: "Okay",
    accessibility: " ",
    warningOnChangeHeadingMsg: "Hang on!",
    warningOnChangeBodyMsg: "Complete all questions before you continue.",
    warningOnChangeCancelButton: "Go Back",
    warningOnChangeContinueButton: "Continue Anyway",
    nextButtonLabel: " ",
    submitButtonLabel: "Done",
    submitCriteriaNotMet: "You skipped at least one question",
    submitTest: "You missed something...",
    submitCriteriaLessThanRequiredAttemptedQuestionsWarning:
      "Go back and answer {{numberOfMoreQuestionsNeeded}} more questions to complete the ladder.",
  },
};

export const genericLearnosityConfig = {
  configuration: {
    onsubmit_redirect_url: false,
  },
  navigation: {
    auto_save: {
      save_interval_duration: 30,
      ui: true,
    },
    skip_submit_confirmation: true,
  },
};

export const trackerLearnosityConfig = {
  questions_api_init_options: {
    show_distractor_rationale: {
      per_question: "always",
      per_response: "always",
    },
  },
  configuration: {
    onsubmit_redirect_url: false,
    idle_timeout: false,
    submit_criteria: {
      type: "attempted",
      threshold: 100,
    },
  },
  navigation: {
    auto_save: {
      save_interval_duration: 30,
      ui: true,
    },
    show_intro: false,
    show_outro: false,
    scrolling_indicator: true,
    warning_on_change: false,
    skip_submit_confirmation: true,
  },
  regions: {
    "top-left": [],
    "top-right": [],
    right: [],
    "bottom-right": [
      { type: "next_button" },
      { type: "horizontaltoc_element" },
      { type: "previous_button" },
    ],
    items: [{ type: "progress_element" }, { type: "slider_element", scrollable_option: false }],
    "bottom-left": [{ type: "accessibility_button" }],
    bottom: [],
  },
  title: "All About Interviews",
  labelBundle: {
    confirm: "Okay",
    continue: "Okay",
    accessibility: "",
    warningOnChangeHeadingMsg: "Hang on!",
    warningOnChangeBodyMsg: "Complete all questions before you continue.",
    warningOnChangeCancelButton: "Go Back",
    warningOnChangeContinueButton: "Continue Anyway",
    submitButtonLabel: "Save",
    submitCriteriaNotMet: "Come back later to complete your hours for this week",
    submitTest: "Progress Saved",
    submitCriteriaLessThanRequiredAttemptedQuestionsWarning: "Click exit above.",
  },
};

// Ladder type arrays

export const ladderTypes = [
  "informational",
  "knowledge-builder",
  "skill-builder",
  "profiler-values",
  "profiler-riasec",
  "tracker",
  "generic",
];

export const scoredLadderTypes = ["knowledge-builder", "skill-builder", "quiz"];

export const profilerLadderTypes = ["profiler-values", "profiler-riasec", "inventory"];

// Type checking functions

export const isGenericLadderType = (ladderType: string): boolean => {
  return ladderType === "generic";
};

export const isGameLadderType = (ladderType: string): boolean => {
  return ladderType === "game";
};

export const isTrackerLadderType = (ladderType: string): boolean => {
  return ladderType === "tracker";
};

export const isRiasecProfilerLadderType = (ladderType: string): boolean => {
  return ladderType === "profiler-riasec";
};

export const isValuesProfilerLadderType = (ladderType: string): boolean => {
  return ladderType === "profiler-values";
};

export const isProfilerLadderType = (ladderType: string): boolean => {
  return profilerLadderTypes.includes(ladderType);
};

export const isScoredLadderType = (ladderType: string): boolean => {
  return scoredLadderTypes.includes(ladderType);
};

export const getLearnosityConfig = (ladderType: string): Record<string, unknown> => {
  if (isGenericLadderType(ladderType)) {
    return genericLearnosityConfig;
  }
  if (isGameLadderType(ladderType)) {
    return gameLearnosityConfig;
  }
  if (isProfilerLadderType(ladderType)) {
    return profilerLearnosityConfig;
  }
  if (isTrackerLadderType(ladderType)) {
    return trackerLearnosityConfig;
  }
  return standardLearnosityConfig;
};

// Scoring functions

export const getTotalMaxScore = (
  scores: Record<string, { score: number; max_score: number }>,
): number => {
  return Object.values(scores).reduce((sum, item) => sum + item.max_score, 0);
};

export const getPointsScored = (
  scores: Record<string, { score: number; max_score: number }>,
): number => {
  return Object.values(scores).reduce((sum, item) => sum + item.score, 0);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getReportingFields = (itemsApp: any): Record<string, unknown> => {
  const tags = itemsApp.getTags();
  const items = itemsApp.getItems();
  const responses = itemsApp.getResponses();

  const result: Record<string, unknown> = {};

  Object.entries(tags).forEach(([itemId, itemTags]: [string, unknown]) => {
    (itemTags as string[]).forEach((tag: string) => {
      if (tag.startsWith("ReportMe:")) {
        const reportKey = tag.substring("ReportMe:".length);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const item = (items as any)[itemId];
        if (item && item.response_ids && item.response_ids.length > 0) {
          const firstResponseId = item.response_ids[0];
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const response = (responses as any)[firstResponseId];
          if (response && response.value) {
            const value = Array.isArray(response.value) ? response.value[0] : response.value;
            result[reportKey] = value;
          }
        }
      }
    });
  });

  return result;
};
